import { ReactNode, useEffect, useState } from 'react';
import i18next from 'i18next';
import Axios from 'axios';
import { debounce } from 'lodash';
import { Field } from 'react-final-form';
import { getUserFullName } from '~src/interfaces/user.interface';
import { CancelReasons } from '~src/services/ApiController';
import { FieldType } from '~src/types';
import SelectInput from '~src/shared/form/select/SelectInput';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';

import { OptionI, AnyArrayOfObj, FetchSearchSuggestions, MapData } from './selectSearch.interfaces';

interface Props {
  labelKey: string;
  mapData: MapData;
  focusedLabelKey: string;
  noItemsTxt: string;
  t: i18next.TFunction;
  inputName: string;
  currItemOpt: OptionI;
  noSelectedOpt: OptionI;
  fetchSearchSuggestions: FetchSearchSuggestions;
}

const SelectSearch = (props: Props) => {
  const {
    labelKey = 'x.manager',
    focusedLabelKey = 'x.search_manager_select.label',
    noItemsTxt = 'x.search_select.no_items',
    mapData = (data: AnyArrayOfObj) =>
      data.map((item) => ({ name: getUserFullName(item.meta), value: item.id })),
  } = props;

  const [foundItems, setFoundItems] = useState(
    props.currItemOpt ? [props.noSelectedOpt, props.currItemOpt] : [props.noSelectedOpt],
  );
  const [isRequestPending, setIsRequestPending] = useState(false);

  const [state, setState] = useState({
    source: Axios.CancelToken.source(),
    cancelToken: Axios.CancelToken,
    labelKey,
  });

  useEffect(() => {
    setFoundItems(props.currItemOpt ? [props.noSelectedOpt, props.currItemOpt] : [props.noSelectedOpt]);
  }, [props.currItemOpt]);

  const { t } = props;

  const debounceWait = 500;
  const createDebounce = () =>
    debounce((value: string) => {
      if (isRequestPending) {
        state.source.cancel(CancelReasons.DEBOUNCE);
        setState({ ...state, source: state.cancelToken.source() });
      }

      setIsRequestPending(true);
      setFoundItems([]);
      const onChangePromise = props.fetchSearchSuggestions(value, state.source.token);

      if (onChangePromise) {
        onChangePromise.then((data) => {
          if (data) {
            setFoundItems([props.noSelectedOpt, ...mapData(data)]);
          }
          setIsRequestPending(false);
        });
      }
    }, debounceWait);

  const debounceSearch = createDebounce();

  const onDropdownVisibleChange = (open: boolean) => {
    if (open) {
      setState({
        ...state,
        labelKey: focusedLabelKey,
      });
      setFoundItems([props.noSelectedOpt]);
    } else {
      setState({ ...state, labelKey });
    }
  };
  const noItemsPlaceholder = () =>
    isRequestPending ? <LoadingSpinner size={10} marginTop={17} dotsMargin={3} /> : t(noItemsTxt);

  const renderSelect = ({
    input,
    items,
    label,
    labelKey,
    defaultValue,
    meta: { touched, error },
  }: FieldType & {
    labelKey: string;
    items: Array<OptionI & Record<string, string>>;
    defaultValue: string;
    fetching: boolean;
  }): ReactNode => (
    <SelectInput
      {...input}
      t={t}
      showSearch
      label={label}
      labelKey={labelKey}
      optList={items}
      defaultValue={defaultValue}
      error={touched ? error : null}
      onSearch={debounceSearch}
      filterOption={false}
      onDropdownVisibleChange={onDropdownVisibleChange}
      notFoundContent={noItemsPlaceholder()}
    />
  );

  const { inputName } = props;
  const defaultValue = props.currItemOpt?.value ?? props.noSelectedOpt.value;

  return (
    <Field
      name={inputName}
      label={t(state.labelKey)}
      labelKey={state.labelKey}
      children={renderSelect}
      items={foundItems}
      defaultValue={defaultValue}
    />
  );
};

export default SelectSearch;
